import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DashboardLayout from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/Shared";
import { OpportunityForm, ApplicantsTable } from "@/components/PortalShared";
import { api } from "@/lib/api";

export function PortalStats({ stats }) {
    if (!stats) return null;
    const cards = [
        { label: "Projects", value: stats.projects },
        { label: "Internships", value: stats.internships },
        { label: "Applicants", value: stats.applicants },
        { label: "Selected", value: stats.selected },
        { label: "Pending Approval", value: stats.pending_approval },
    ];
    return (
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="portal-stats">
            {cards.map((c) => (
                <div key={c.label} className="card-soft p-5">
                    <p className="font-display text-3xl font-bold text-primary">{c.value}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{c.label}</p>
                </div>
            ))}
        </div>
    );
}

export function OpportunitiesPanel({ data, reload }) {
    return (
        <div className="space-y-8">
            <div>
                <h3 className="font-display text-lg font-semibold">Internship Programs</h3>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {data.internships.map((it) => (
                        <div key={it.id} className="card-soft p-5" data-testid={`my-internship-${it.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="font-display text-sm font-semibold">{it.title}</p>
                                    <p className="mt-1 text-xs text-muted-foreground">{it.domain} · {it.duration_months} months · ${it.fee_usd}</p>
                                </div>
                                <StatusBadge status={it.status} />
                            </div>
                        </div>
                    ))}
                    {data.internships.length === 0 && <p className="text-sm text-muted-foreground">No internship programs yet.</p>}
                </div>
            </div>
            <div>
                <h3 className="font-display text-lg font-semibold">Research Projects</h3>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {data.projects.map((p) => (
                        <div key={p.id} className="card-soft p-5" data-testid={`my-project-${p.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="font-display text-sm font-semibold">{p.title}</p>
                                    <p className="mt-1 text-xs text-muted-foreground">{p.domain} · {p.duration}</p>
                                </div>
                                <StatusBadge status={p.status} />
                            </div>
                        </div>
                    ))}
                    {data.projects.length === 0 && <p className="text-sm text-muted-foreground">No projects yet.</p>}
                </div>
            </div>
        </div>
    );
}

export default function FacultyPortal() {
    const [stats, setStats] = useState(null);
    const [opps, setOpps] = useState({ projects: [], internships: [] });
    const [applicants, setApplicants] = useState([]);

    const load = () => {
        api.get("/faculty-portal/overview").then((r) => setStats(r.data)).catch(() => {});
        api.get("/faculty-portal/opportunities").then((r) => setOpps(r.data)).catch(() => {});
        api.get("/faculty-portal/applicants").then((r) => setApplicants(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    return (
        <DashboardLayout title="Faculty Portal">
            <div className="space-y-8" data-testid="faculty-portal">
                <PortalStats stats={stats} />
                <Tabs defaultValue="opportunities">
                    <TabsList data-testid="faculty-portal-tabs">
                        <TabsTrigger value="opportunities" data-testid="tab-opportunities">My Opportunities</TabsTrigger>
                        <TabsTrigger value="applicants" data-testid="tab-applicants">Applicants ({applicants.length})</TabsTrigger>
                    </TabsList>
                    <TabsContent value="opportunities" className="mt-6 space-y-6">
                        <div className="flex gap-3">
                            <OpportunityForm kind="project" portalPrefix="faculty-portal" onCreated={load} />
                            <OpportunityForm kind="internship" portalPrefix="faculty-portal" onCreated={load} />
                        </div>
                        <OpportunitiesPanel data={opps} reload={load} />
                    </TabsContent>
                    <TabsContent value="applicants" className="mt-6">
                        <ApplicantsTable applicants={applicants} onStatusChange={load} />
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
}
